<script setup>
import { computed, ref } from 'vue'
import { AUTH_TEXT_GROUPS, AUTH_TEXT_FIELDS, AUTH_TEXT_MAX, defaultAuthText } from '@/components/Sites/siteAuthTexts'

// Theme › Texts — sign-in, sign-up, reset-password and header button texts
// (runtime `authTexts`, v0.20), on both templates. An empty field shows what
// viewers see today, which is also the placeholder.
const props = defineProps({
	template: {
		type: String,
		default: 'catalog',
	},
	/** Saved settings, for the older single-label values still in effect. */
	settings: {
		type: Object,
		default: () => ({}),
	},
	disabled: {
		type: Boolean,
		default: false,
	},
})
const texts = defineModel({ type: Object, required: true })

const query = ref('')
const fallback = (field) => defaultAuthText(field, props.template, props.settings)
const matches = (field) => {
	const q = query.value.trim().toLowerCase()
	if (!q) return true
	const group = AUTH_TEXT_GROUPS.find((g) => g.id === field.group)?.title || ''
	return `${fallback(field)} ${field.where || ''} ${group} ${texts.value[field.key] || ''}`.toLowerCase().includes(q)
}
const groups = computed(() => AUTH_TEXT_GROUPS.map((g) => {
	const fields = AUTH_TEXT_FIELDS.filter((f) => f.group === g.id)
	return { ...g, fields: fields.filter(matches), overridden: fields.filter((f) => String(texts.value[f.key] || '').trim()).length }
}).filter((g) => g.fields.length))
const set = (key, value) => { texts.value = { ...texts.value, [key]: value } }
const tooLong = (key) => String(texts.value[key] || '').trim().length > AUTH_TEXT_MAX
</script>

<template>
	<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="theme-auth-texts">
		<div class="mb-3 flex flex-wrap items-end justify-between gap-3">
			<div class="min-w-0 flex-1">
				<h3 class="mb-1 text-sm font-semibold text-white">Sign in &amp; sign up</h3>
				<p class="mb-0 text-tiny text-surface-7">Texts on the sign-in, sign-up and reset-password pages and the header buttons. Empty fields keep the wording shown in grey.</p>
			</div>
			<input v-model="query" type="search" placeholder="Find a label…" aria-label="Find a sign-in or sign-up label"
				class="w-full rounded-md border border-surface-5 bg-surface-3 px-3 py-1.5 text-sm text-white placeholder:text-surface-6 focus:border-main focus:outline-none sm:w-56" data-testid="auth-texts-search">
		</div>
		<div class="flex flex-col gap-2">
			<details v-for="(g, gi) in groups" :key="g.id" class="rounded-lg bg-surface-3" :open="gi === 0 || Boolean(query.trim())" :data-testid="`auth-texts-group-${g.id}`">
				<summary class="flex cursor-pointer select-none items-center justify-between gap-3 px-3 py-2.5 text-sm font-medium text-white">
					<span>{{ g.title }}</span>
					<span class="text-tiny font-normal text-surface-7">{{ g.overridden ? `${g.overridden} customised` : g.description }}</span>
				</summary>
				<div class="flex flex-col gap-2 border-t border-surface-4 px-3 py-3">
					<div v-for="f in g.fields" :key="f.key" class="min-w-0" :data-testid="`auth-texts-field-${f.key}`">
						<label :for="`auth-texts-${f.key}`" class="mb-1 block truncate text-tiny text-surface-7" :title="`${fallback(f)} · ${f.where}`">
							{{ fallback(f) }}<span class="text-surface-6"> · {{ f.where }}</span>
						</label>
						<input :id="`auth-texts-${f.key}`" :value="texts[f.key] || ''" type="text" :placeholder="fallback(f)" :maxlength="AUTH_TEXT_MAX + 20" :disabled="disabled"
							class="w-full rounded-md border bg-surface-2 px-3 py-1.5 text-sm text-white placeholder:text-surface-6 focus:border-main focus:outline-none"
							:class="tooLong(f.key) ? 'border-error' : 'border-surface-5'" @input="set(f.key, $event.target.value)">
						<p v-if="tooLong(f.key)" class="mb-0 mt-1 text-tiny text-error">Up to {{ AUTH_TEXT_MAX }} characters</p>
					</div>
				</div>
			</details>
			<p v-if="!groups.length" class="mb-0 px-1 text-tiny text-surface-7">No label matches “{{ query }}”.</p>
		</div>
	</section>
</template>
